import React, { Fragment } from "react";
import { Col, Label, Row } from "reactstrap";
import { CustomerPepDeclarationProps } from "./types";
import { flagCheckboxStyle, flagLabelStyle, flagSectionStyle } from "./flagSectionStyles";

/** Member-type codes, mirroring SetupCategories/PEP_MEMBER_TYPE in CRM. */
export const PEP_MEMBER_TYPE_IS_PEP = "001";
export const PEP_MEMBER_TYPE_RELATED_TO_PEP = "002";

const subTextStyle: React.CSSProperties = { fontSize: "0.85em", color: "#666", marginTop: "6px" };

/**
 * Sub-label under a checked declaration checkbox: the resolved English label for the declared PEP
 * type or relation. Falls back to the raw code so an unmapped code still tells compliance
 * something, and renders nothing when neither is present.
 */
const declaredPepDetail = (label?: string | null, code?: string | null) => {
    const detail = label ?? code;

    if (!detail) return null;

    return <div style={subTextStyle}>({detail})</div>;
};

/**
 * Read-only view of the PEP declaration the customer made during onboarding. This is what the
 * customer stated about themselves - not the risk engine's screening result (isPEPCalculated) and
 * not compliance's final decision (isPEP), both of which have their own sections.
 *
 * The section is wrapped in the yellow warning box when the customer declared themselves a PEP or
 * related to one, so a reviewer cannot miss it. A "not a PEP" declaration (999) and a customer with
 * no declaration at all are both unremarkable, so neither is highlighted.
 *
 * Renders as a full-width column of the scorecard's existing Row, so it must be used inside one.
 */
export const CustomerPepDeclaration: React.FC<CustomerPepDeclarationProps> = ({ declaration }) => {
    const isDeclaredPep = declaration?.pepMemberType === PEP_MEMBER_TYPE_IS_PEP;
    const isDeclaredRelatedToPep = declaration?.pepMemberType === PEP_MEMBER_TYPE_RELATED_TO_PEP;

    return (
        <Col sm={12}>
            <div style={flagSectionStyle(isDeclaredPep || isDeclaredRelatedToPep)} data-testid="pep-declaration-section">
                <Label style={{ fontWeight: "bold", fontSize: "1.1em" }}>Customer PEP Declaration (Onboarding)</Label>
                <Row>
                    {declaration ? (
                        <Fragment>
                            <Col className="p-2.5" sm={12} md={2} lg={2} xl={1}>
                                <Label for="DeclaredIsPEP" style={{ ...flagLabelStyle, fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Is PEP
                                </Label>
                                <br />
                                <input type="checkbox" style={flagCheckboxStyle} checked={isDeclaredPep} readOnly={true} tabIndex={-1} aria-disabled={true} id="DeclaredIsPEP"></input>
                                {isDeclaredPep && declaredPepDetail(declaration.pepTypeLabel, declaration.pepType)}
                            </Col>
                            <Col className="p-2.5" sm={12} md={4} lg={4} xl={3}>
                                <Label for="DeclaredIsRelatedToPEP" style={{ ...flagLabelStyle, fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Is Related to a PEP
                                </Label>
                                <br />
                                <input
                                    type="checkbox"
                                    style={flagCheckboxStyle}
                                    checked={isDeclaredRelatedToPep}
                                    readOnly={true}
                                    tabIndex={-1}
                                    aria-disabled={true}
                                    id="DeclaredIsRelatedToPEP"
                                ></input>
                                {isDeclaredRelatedToPep && declaredPepDetail(declaration.pepRelationLabel, declaration.pepRelation)}
                            </Col>
                        </Fragment>
                    ) : (
                        <Col className="p-2.5" sm={12}>
                            <div style={subTextStyle}>(No PEP declaration data available for this customer)</div>
                        </Col>
                    )}
                </Row>
            </div>
        </Col>
    );
};
